import React, { useContext, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Col, Row } from 'react-bootstrap'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetCrossClientQueryExecutionQuery } from '../../../store/crossClient/crossClient'
import {
  useGetAccountSchedulingSettingsQuery,
  useGetSchedulingEventTypesQuery,
  useGetSchedulingLocationsQuery,
  useGetUserSchedulingLocationsQuery,
  useGetUserSchedulingSettingsQuery,
} from '../../../store/scheduling/scheduling'
import { type LocationRef, MeetingMode } from '../../../models/Scheduling'
import { Spinner } from '../../../components/Spinner/Spinner'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { Sticky } from '../../../components/Sticky'
import { LocationSelect } from '../../Scheduling/flows/form/LocationSelect'
import { resolveLocationPreviewId } from '../../Scheduling/flows/form/scheduleHelpers'
import { ccqMatchedListPath, ccqResultsSelectionState } from '../Results/Run/ccqResultsSelection'
import { SendBookingInvitationsHeader } from './SendBookingInvitationsHeader'
import { readSendBookingInvitationsState, type SendBookingInvitationsTarget } from './sendBookingInvitationsRoute'
import {
  LOCATION_DESCRIPTION,
  LOCATION_FIXED_NOTE,
  LOCATION_TITLE,
  SEND_BOOKING_INVITATIONS_CLIENTS_ERROR,
  SEND_BOOKING_INVITATIONS_NO_CLIENTS,
  SEND_BOOKING_INVITATIONS_TITLE,
  sendBookingInvitationsSubtitle,
} from './sendBookingInvitationsCopy'
import { FormSection } from './FormSection'
import { MeetingOwnerSection } from './MeetingOwnerSection'
import { EventTypeSection } from './EventTypeSection'
import { InvitationPreview } from './InvitationPreview'
import { SendPanel } from './SendPanel'
import { firstSelectableEventType, type HostSelection } from './eventTypeCoverage'
import { FallbackModes, MeetingOwnerModes, type FallbackMode, type MeetingOwnerMode } from './meetingOwnerMode'
import { useSendBookingInvitationsOwners } from './useSendBookingInvitationsOwners'
import { useSendBookingInvitationsClientIds } from './useSendBookingInvitationsClientIds'
import { useElementHeight } from './useElementHeight'
import styles from './SendBookingInvitationsView.module.scss'

const EMPTY_CLIENT_IDS: string[] = []

export const SendBookingInvitationsView: React.FC = () => {
  const { executionId } = useParams()
  const { state } = useLocation()
  useDocumentTitle(SEND_BOOKING_INVITATIONS_TITLE)
  const sendBookingInvitationsState = useMemo(() => readSendBookingInvitationsState(state), [state])

  if (!executionId) {
    return <div>No query provided</div>
  }

  if (!sendBookingInvitationsState) {
    return <Navigate replace to={`/cross-client/executions/${executionId}`} />
  }

  return (
    <SendBookingInvitationsContent
      key={executionId}
      executionId={executionId}
      target={sendBookingInvitationsState.target}
      count={sendBookingInvitationsState.count}
    />
  )
}

interface SendBookingInvitationsContentProps {
  executionId: string
  target: SendBookingInvitationsTarget
  count: number
}

const SendBookingInvitationsContent: React.FC<SendBookingInvitationsContentProps> = ({ executionId, target, count }) => {
  const { portalElement } = useContext(HeaderContext)
  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)

  const { data: execution, isLoading, isError } = useGetCrossClientQueryExecutionQuery({ accountId, executionId })
  const { clientIds: resolvedClientIds, resultIdByClientId, isError: clientIdsError } = useSendBookingInvitationsClientIds(executionId, target)
  const navigate = useNavigate()
  const resultsLinkFor = (linkClientIds: string[]): (() => void) | undefined => {
    const resultIds = linkClientIds.flatMap((clientId) => (resultIdByClientId[clientId] ? [resultIdByClientId[clientId]] : []))
    if (linkClientIds.length === 0 || resultIds.length < linkClientIds.length) {
      return undefined
    }
    return () => navigate(ccqMatchedListPath(executionId), { state: ccqResultsSelectionState(resultIds) })
  }
  const owners = useSendBookingInvitationsOwners(resolvedClientIds)
  const clientIds = resolvedClientIds ?? EMPTY_CLIENT_IDS

  const [ownerMode, setOwnerMode] = useState<MeetingOwnerMode>(MeetingOwnerModes.EACH)
  const [ownerUserId, setOwnerUserId] = useState(currentUser.id)
  const [fallback, setFallback] = useState<FallbackMode>(FallbackModes.SKIP)
  const [backupOwnerId, setBackupOwnerId] = useState(currentUser.id)
  const [pickedEventTypeId, setPickedEventTypeId] = useState<string | null>(null)
  const [pickedLocations, setPickedLocations] = useState<Record<string, LocationRef | null>>({})
  const { ref: sendBarRef, height: sendBarHeight } = useElementHeight<HTMLDivElement>()
  const hostSelection = useMemo<HostSelection>(
    () => ({ mode: ownerMode, ownerUserId, fallback, backupOwnerId, sharedUserIds: owners.sharedUserIds }),
    [ownerMode, ownerUserId, fallback, backupOwnerId, owners.sharedUserIds],
  )

  const isSingleOwner = ownerMode === MeetingOwnerModes.SINGLE
  const singleOwnerQuery = isSingleOwner ? { accountId, userId: ownerUserId } : skipToken
  const { data: eventTypes } = useGetSchedulingEventTypesQuery({ accountId })
  const { data: accountSettings } = useGetAccountSchedulingSettingsQuery({ accountId })
  const { data: accountLocations } = useGetSchedulingLocationsQuery({ accountId })
  const { data: singleOwnerLocations } = useGetUserSchedulingLocationsQuery(singleOwnerQuery)
  const { data: singleOwnerSettings } = useGetUserSchedulingSettingsQuery(singleOwnerQuery)

  const eventTypeId = pickedEventTypeId ?? firstSelectableEventType(eventTypes ?? [])?.id ?? null
  const eventType = eventTypes?.find((candidate) => candidate.id === eventTypeId)
  const singleOwner = isSingleOwner ? owners.teammates.find((teammate) => teammate.id === ownerUserId) : undefined
  const userLocations = isSingleOwner ? (singleOwnerLocations ?? []) : []
  const userDefaultLocationId = isSingleOwner ? singleOwnerSettings?.defaults.locationId : null

  const defaultLocation: LocationRef | null = eventType?.location
    ? {
        kind: eventType.location.kind,
        id: resolveLocationPreviewId(eventType.location, accountSettings?.defaults.locationId, userDefaultLocationId),
      }
    : null
  const location = eventTypeId && eventTypeId in pickedLocations ? pickedLocations[eventTypeId] : defaultLocation

  const handleModeChange = (mode: MeetingOwnerMode) => {
    setOwnerMode(mode)
    setPickedLocations({})
  }

  const handleOwnerUserIdChange = (userId: string) => {
    setOwnerUserId(userId)
    setPickedLocations({})
  }

  if (isLoading) {
    return (
      <div className='d-flex flex-column align-items-center justify-content-center gap-3 mt-10'>
        <Spinner />
      </div>
    )
  }

  if (isError) {
    return <div className='paragraph-medium zocks-text-gray-700 p-4'>Failed to load the query. Please try again.</div>
  }

  if (!execution) {
    return <div className='paragraph-medium zocks-text-gray-700 p-4'>Query not found</div>
  }

  const backTo = `/cross-client/executions/${executionId}`
  const header = portalElement ? createPortal(<SendBookingInvitationsHeader backTo={backTo} />, portalElement) : null

  if (clientIdsError || resolvedClientIds?.length === 0) {
    return (
      <div className='truly-full-size-content d-flex flex-column py-4 zocks-px-2.0'>
        {header}
        <h1 className='h3 mb-0 zocks-text-gray-900'>{SEND_BOOKING_INVITATIONS_TITLE}</h1>
        <span className='paragraph-medium zocks-text-gray-700'>
          {clientIdsError ? SEND_BOOKING_INVITATIONS_CLIENTS_ERROR : SEND_BOOKING_INVITATIONS_NO_CLIENTS}
        </span>
      </div>
    )
  }

  return (
    <div className='truly-full-size-content d-flex flex-column min-vh-100 py-4 zocks-px-2.0'>
      {header}
      <div className='flex-grow-1'>
        <Row className={styles.columns}>
          <Col lg={6} className='d-flex flex-column zocks-gap-2.5' style={{ paddingBottom: `calc(${sendBarHeight}px + var(--zocks-1\\.50))` }}>
            <div className='d-flex flex-column'>
              <h1 className='h3 mb-0 zocks-text-gray-900'>{SEND_BOOKING_INVITATIONS_TITLE}</h1>
              <span className='paragraph-medium zocks-text-gray-600'>
                {sendBookingInvitationsSubtitle(resolvedClientIds?.length ?? count, execution.userQuestion)}
              </span>
            </div>
            <div className='d-flex flex-column zocks-gap-2.5'>
              <MeetingOwnerSection
                owners={owners}
                mode={ownerMode}
                onModeChange={handleModeChange}
                ownerUserId={ownerUserId}
                onOwnerUserIdChange={handleOwnerUserIdChange}
                fallback={fallback}
                onFallbackChange={setFallback}
                backupOwnerId={backupOwnerId}
                onBackupOwnerIdChange={setBackupOwnerId}
                resultsLinkFor={resultsLinkFor}
              />
              <EventTypeSection
                owners={owners}
                hostSelection={hostSelection}
                eventTypeId={eventTypeId}
                onEventTypeIdChange={setPickedEventTypeId}
                resultsLinkFor={resultsLinkFor}
              />
              {eventType ? (
                <>
                  {eventType.meetingMode === MeetingMode.IN_PERSON ? (
                    <FormSection title={LOCATION_TITLE} description={LOCATION_DESCRIPTION}>
                      <LocationSelect
                        bare
                        eventType={eventType}
                        accountDefaultLocationId={accountSettings?.defaults.locationId}
                        userDefaultLocationId={userDefaultLocationId}
                        accountLocations={accountLocations ?? []}
                        userLocations={userLocations}
                        ownerUserId={isSingleOwner ? ownerUserId : ''}
                        ownerFirstName={(singleOwner?.name ?? '').split(' ')[0]}
                        isSchedulingForSelf={isSingleOwner && ownerUserId === currentUser.id}
                        location={location}
                        customLocation={null}
                        fixedNote={LOCATION_FIXED_NOTE}
                        allowCustomLocation={false}
                        onChange={(next) => {
                          const nextLocation = next.location
                          if (nextLocation !== undefined) {
                            setPickedLocations((picked) => ({ ...picked, [eventType.id]: nextLocation }))
                          }
                        }}
                      />
                    </FormSection>
                  ) : null}
                </>
              ) : null}
            </div>
          </Col>
          <Col lg={6}>
            <Sticky mode='top'>
              <InvitationPreview clientIds={clientIds} owners={owners} hostSelection={hostSelection} eventTypeId={eventTypeId} location={location} />
            </Sticky>
          </Col>
        </Row>
      </div>
      <SendPanel
        executionId={executionId}
        clientIds={clientIds}
        owners={owners}
        hostSelection={hostSelection}
        eventTypeId={eventTypeId}
        eventTypeName={eventType?.name ?? ''}
        meetingMode={eventType?.meetingMode ?? null}
        location={location}
        barRef={sendBarRef}
      />
    </div>
  )
}
